import { PlusOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Input, Tag } from 'antd';
import FormField from 'components/FormField';
import { Formik, FormikConfig } from 'formik';
import useSmartField from 'hooks/useSmartField';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { FV } from './share';

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  > * {
    height: 32px;
    margin-bottom: 0;
  }
  > span {
    display: flex;
    align-items: center;
  }
`;

const countLimit = 8;

function KeywordListField({ disabled = false }: { disabled?: boolean }) {
  const { t } = useTranslate();

  const {
    field: { value, onChange },
  } = useSmartField<FV['keywordList']>('keywordList');

  const list = value.map((word, index) => {
    return (
      <span key={word}>
        <Tag
          closable={!disabled}
          onClose={() => {
            onChange([...value.slice(0, index), ...value.slice(index + 1)]);
          }}
        >
          {word}
        </Tag>
      </span>
    );
  });

  const [editing, setEditing] = useState(false);

  const edit = (() => {
    if (value.length >= countLimit || disabled) {
      return null;
    }

    if (editing) {
      return (
        <KeywordForm
          existList={value}
          initialValues={{ value: '' }}
          onSubmit={(values) => {
            onChange([...value, values.value]);

            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      );
    }

    return (
      <span>
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => setEditing(true)}>
          {t('marketing.新增')}
        </Button>
      </span>
    );
  })();

  return (
    <FormField<FV['keywordList']>
      name="keywordList"
      required
      label={`${t('aide.暗号')}(${value.length}/${countLimit})`}
      validate={(value) => (value.length > 0 ? undefined : t('aide.请添加暗号'))}
    >
      <Container>
        {list}
        {edit}
      </Container>
    </FormField>
  );
}

interface KeywordFormProps extends FormikConfig<{ value: string }> {
  existList: string[];
  onCancel: () => void;
}

const keywordLengthLimit = 30;

function KeywordForm(props: KeywordFormProps) {
  const { existList, onCancel, ...otherProps } = props;

  const { t } = useTranslate();

  return (
    <Formik enableReinitialize {...otherProps}>
      {(formProps) => {
        const { handleSubmit } = formProps;

        return (
          <FormField<string>
            name="value"
            validate={(value) => {
              if (!value || !value.trim()) {
                return t('department.请输入x', { x: t('aide.暗号') });
              }

              if (existList.includes(value)) {
                return t('aide.暗号重复');
              }

              return undefined;
            }}
          >
            {({ field }) => {
              return (
                <Input
                  {...field}
                  showCount
                  autoFocus
                  style={{ width: '380px' }}
                  maxLength={keywordLengthLimit}
                  placeholder={t('aide.输入后按Enter创建')}
                  onPressEnter={() => handleSubmit()}
                  onBlur={({ target: { value } }) => {
                    if (!value) {
                      onCancel();
                    } else {
                      handleSubmit();
                    }
                  }}
                />
              );
            }}
          </FormField>
        );
      }}
    </Formik>
  );
}

export default KeywordListField;
